import React, {useState, useEffect} from 'react';
import {StyleSheet, ScrollView, RefreshControl, Platform} from 'react-native';
import {Box, Heading, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import * as Keychain from '../../../../utils/keychain';
import {
  BalanceSummaryIcon,
  ClientsIcon as SalesmanIcon,
  SearchCircleIcon,
} from '../../../../components/icons';
import DashboardTemplate from '../../../../components/templates/dashboard';
import {useContextState as userContext} from '../../../../context/user';
import useToast from '../../../../hooks';
import {
  DashboardItem,
  SummaryAccountItem,
} from '../../../../components/dashboard';
import {StackParamList} from '../../../../navigators';
import {SpinnerModal} from '../../../../components/modals';
import {message as t} from '../../../../utils/messages';
import useClientContext from '../../../../context/client/Context';

type ScreenProps = NativeStackScreenProps<StackParamList, 'HomeClient'>;

export const HomeClientScreen = ({navigation}: ScreenProps) => {
  const {signOff, notifications, fetchNotificationList, profile} =
    userContext();

  const {
    fetchBalanceSheet,
    balanceSheet,
    fetchSemaphoreItems,
    semaphoreItems,
    fetchResources,
  } = useClientContext();

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const {showServerError} = useToast();

  const getNotifications = async () => {
    await fetchNotificationList();
  };

  const getIndicators = async () => {
    if (await fetchBalanceSheet()) {
      if (await fetchSemaphoreItems()) {
        return;
      }
    }
    throw new Error('Internal error');
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      await fetchResources();
      await getNotifications();
      await getIndicators();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const refreshData = async () => {
    await getData();
    setRefreshing(false);
  };

  useEffect(() => {
    if (balanceSheet) {
      return;
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSheet]);

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const handleTapNotifications = () => {
    navigation.navigate('Notifications');
  };

  const handleShowProfile = () => {
    navigation.navigate('Profile');
  };

  const handleLogOut = () => {
    signOff();
    Keychain.resetGenericPassword();
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return '';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleTapProdile = () => {
    handleShowProfile();
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  const goToDashboard = () => {
    navigation.navigate('DashboardClient');
  };

  const goToTrackingFilter = () => {
    navigation.navigate('TrackingFilterClient');
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      notifications={notifications ? notifications : []}
      onTapNotifications={() => handleTapNotifications()}
      onTapLogout={() => handleLogOut()}
      onTapProfile={() => handleTapProdile()}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={balanceSheet && semaphoreItems ? t('updating') : undefined}
        />
        {
          <Box borderRadius="md" marginTop={22}>
            <VStack space={3} justifyContent="center" marginX={4}>
              <DashboardItem
                label="dashboard-summary-account-title"
                icon={
                  <BalanceSummaryIcon width={40} style={styles.balanceIcon} />
                }
                shadow={true}>
                <SummaryAccountItem
                  localBalance={!isLoading ? balanceSheet?.local : '---'}
                  foreignBalance={!isLoading ? balanceSheet?.foreign : '---'}
                />
              </DashboardItem>
              <DashboardItem
                paddingTop={4}
                paddingBottom={2}
                title={
                  <Heading
                    color={'#06355E'}
                    size="sm"
                    fontWeight="extrabold"
                    fontStyle="italic"
                    textAlign="left"
                    marginTop={3}>
                    {t('client-home-dashboard-access').toUpperCase()}{' '}
                  </Heading>
                }
                icon={<SearchCircleIcon width={40} />}
                onPress={goToDashboard}
              />
              {!(Platform.OS === 'web') && (
                <DashboardItem
                  title={
                    <Heading
                      color={'#06355E'}
                      size="sm"
                      fontWeight="extrabold"
                      fontStyle="italic"
                      textAlign="left"
                      marginTop={6}>
                      {t('client-home-tracking-access').toUpperCase()}{' '}
                    </Heading>
                  }
                  icon={<SalesmanIcon width={40} />}
                  onPress={goToTrackingFilter}
                />
              )}
            </VStack>
          </Box>
        }
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});
